import { useState, useEffect, useRef } from 'react';
import { ChevronDown, List } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

interface Heading {
  id: string;
  text: string;
  level: number;
}

interface TableOfContentsProps {
  content: string;
}

export const TableOfContents = ({ content }: TableOfContentsProps) => {
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [activeId, setActiveId] = useState<string>('');
  const [isOpen, setIsOpen] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Parse headings from HTML content
  useEffect(() => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');
    const elements = doc.querySelectorAll('h2, h3');
    
    const parsed: Heading[] = Array.from(elements).map((el, index) => {
      const id = el.textContent?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || `heading-${index}`;
      return {
        id,
        text: el.textContent || '',
        level: parseInt(el.tagName.charAt(1)),
      };
    });
    
    setHeadings(parsed);
  }, [content]);

  // Track active section
  useEffect(() => {
    if (headings.length === 0) return;

    // Add IDs to actual DOM headings
    const articleHeadings = document.querySelectorAll('article h2, article h3');
    articleHeadings.forEach((heading, index) => {
      if (headings[index]) {
        heading.id = headings[index].id;
      }
    });

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: '-100px 0px -80% 0px' }
    );

    articleHeadings.forEach((heading) => observerRef.current?.observe(heading));

    return () => observerRef.current?.disconnect();
  }, [headings]);

  const scrollToHeading = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsOpen(false);
    }
  };

  if (headings.length === 0) return null;

  const TocList = () => (
    <nav className="space-y-2">
      {headings.map((heading) => (
        <button
          key={heading.id}
          onClick={() => scrollToHeading(heading.id)}
          className={cn(
            "block w-full text-left text-sm transition-all duration-200",
            heading.level === 3 && "pl-4",
            activeId === heading.id
              ? "text-[#00C2FF] font-medium"
              : "text-white/60 hover:text-white/90"
          )}
        >
          {heading.text}
        </button>
      ))}
    </nav>
  );

  return (
    <>
      {/* Desktop: Sticky Sidebar */}
      <div className="hidden lg:block sticky top-28">
        <div className="bg-black/40 backdrop-blur-md border border-white/10 rounded-xl p-5">
          <h4 className="text-sm font-semibold text-white/80 uppercase tracking-wider mb-4 flex items-center gap-2">
            <List className="w-4 h-4" />
            Contents
          </h4>
          <TocList />
        </div>
      </div>

      {/* Mobile: Collapsible Dropdown */}
      <div className="lg:hidden mb-6">
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CollapsibleTrigger className="w-full bg-black/40 backdrop-blur-md border border-white/10 rounded-xl p-4 flex items-center justify-between">
            <span className="text-sm font-semibold text-white/80 uppercase tracking-wider flex items-center gap-2">
              <List className="w-4 h-4" />
              Table of Contents
            </span>
            <ChevronDown className={cn(
              "w-4 h-4 text-white/60 transition-transform duration-200",
              isOpen && "rotate-180"
            )} />
          </CollapsibleTrigger>
          <CollapsibleContent className="bg-black/40 backdrop-blur-md border border-t-0 border-white/10 rounded-b-xl p-4 -mt-2">
            <TocList />
          </CollapsibleContent>
        </Collapsible>
      </div>
    </>
  );
};
